import { computed, ref } from "vue";
import axios from "axios";

const parseDateValue = (value) => {
  if (!value && value !== 0) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "number") {
    const dateFromNumber = new Date(value);
    return Number.isNaN(dateFromNumber.getTime()) ? null : dateFromNumber;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const directDate = new Date(trimmed);
    if (!Number.isNaN(directDate.getTime())) {
      return directDate;
    }

    const normalized = trimmed.endsWith("Z") ? trimmed : `${trimmed}Z`;
    const fallback = new Date(normalized);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  return null;
};

const pickFirstValue = (source, keys) => {
  if (!source) {
    return null;
  }
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const value = source[key];
      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }
  }
  return null;
};

export const getAllyValidity = (ally) => {
  const fromRaw = pickFirstValue(ally, [
    "valid_from",
    "validFrom",
    "valid_from_at",
    "valid_from_date",
    "benefit_valid_from",
    "benefitValidFrom",
    "benefit_start",
    "benefit_start_at",
    "agreement_valid_from",
    "agreementValidFrom",
  ]);

  const toRaw = pickFirstValue(ally, [
    "valid_to",
    "validTo",
    "valid_to_at",
    "valid_to_date",
    "benefit_valid_to",
    "benefitValidTo",
    "benefit_end",
    "benefit_end_at",
    "validity_of_agreement",
    "agreement_valid_to",
    "agreementValidTo",
  ]);

  return {
    from: parseDateValue(fromRaw),
    to: parseDateValue(toRaw),
  };
};

export const isAllyInForce = (ally, referenceDate = new Date()) => {
  if (!ally) {
    return false;
  }

  const now = referenceDate instanceof Date ? referenceDate : new Date(referenceDate);
  const { from, to } = getAllyValidity(ally);

  if (from && now < from) {
    return false;
  }

  if (to && now > to) {
    return false;
  }

  const allyEnabled = ally.enabled !== false && ally.disabled !== true;
  const benefitEnabled = ally.benefit_enabled !== false && ally.benefit_disabled !== true;

  return allyEnabled && benefitEnabled;
};

const dedupeByKey = (items, keyResolver) => {
  const map = new Map();
  items.forEach((item) => {
    const key = keyResolver(item);
    if (key) {
      map.set(key, item);
    }
  });
  return Array.from(map.values());
};

export const useAllies = () => {
  const allies = ref([]);
  const pagination = ref({
    current_page: 1,
    per_page: 12,
    total: 0,
    last_page: 1,
  });
  const categories = ref([]);
  const cities = ref([]);

  const isLoadingList = ref(false);
  const listError = ref(null);

  const isLoadingDetail = ref(false);
  const detailError = ref(null);

  const detailCache = new Map();

  const availableCategories = computed(() =>
    categories.value.map((category) => ({
      label: category?.name || category?.label || category?.text || "",
      value: category?.id || category?.slug || category?.value || category,
      raw: category,
    }))
  );

  const availableCities = computed(() =>
    cities.value.map((city) => ({
      label: city?.name || city?.label || city?.text || "",
      value: city?.id || city?.slug || city?.value || city,
      raw: city,
    }))
  );

  const extractFilters = (responsePayload, items) => {
    const filters = responsePayload?.filters || {};

    if (Array.isArray(filters.categories)) {
      categories.value = filters.categories;
    } else if (Array.isArray(responsePayload?.categories)) {
      categories.value = responsePayload.categories;
    } else if (Array.isArray(items)) {
      const derivedCategories = dedupeByKey(
        items
          .map((ally) => ally.category || ally.categories || null)
          .flatMap((item) => (Array.isArray(item) ? item : item ? [item] : [])),
        (category) => category?.id || category?.slug || category?.name
      );
      if (derivedCategories.length > 0) {
        categories.value = derivedCategories;
      }
    }

    if (Array.isArray(filters.cities)) {
      cities.value = filters.cities;
    } else if (Array.isArray(responsePayload?.cities)) {
      cities.value = responsePayload.cities;
    } else if (Array.isArray(items)) {
      const derivedCities = dedupeByKey(
        items.map((ally) => ally.city || ally.cities || null).filter(Boolean),
        (city) => city?.id || city?.slug || city?.name
      );
      if (derivedCities.length > 0) {
        cities.value = derivedCities;
      }
    }
  };

  const fetchAlliesList = async (params = {}) => {
    isLoadingList.value = true;
    listError.value = null;

    try {
      const { data } = await axios.get("/api/aliados", {
        params: {
          page: pagination.value.current_page,
          per_page: pagination.value.per_page,
          ...params,
        },
      });

      const items = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      const meta = data?.meta || data?.pagination || null;

      allies.value = items;

      if (meta) {
        pagination.value = {
          current_page: meta.current_page || meta.page || 1,
          per_page: meta.per_page || meta.limit || pagination.value.per_page,
          total: meta.total || meta.total_items || items.length,
          last_page: meta.last_page || meta.total_pages || 1,
        };
      } else {
        pagination.value = {
          current_page: 1,
          per_page: items.length || pagination.value.per_page,
          total: items.length,
          last_page: 1,
        };
      }

      extractFilters(data, items);

      return items;
    } catch (error) {
      listError.value = error;
      throw error;
    } finally {
      isLoadingList.value = false;
    }
  };

  const fetchAllyDetail = async (allyId) => {
    if (!allyId) {
      return null;
    }

    if (detailCache.has(allyId)) {
      return detailCache.get(allyId);
    }

    isLoadingDetail.value = true;
    detailError.value = null;

    try {
      const { data } = await axios.get(`/api/aliados/${allyId}`);
      const detail = data?.data || data || null;
      if (detail?.id) {
        detailCache.set(detail.id, detail);
      }
      return detail;
    } catch (error) {
      detailError.value = error;
      throw error;
    } finally {
      isLoadingDetail.value = false;
    }
  };

  const fetchAllyByCode = async (allyCode) => {
    if (!allyCode) {
      return null;
    }

    try {
      const { data } = await axios.get("/api/aliados", {
        params: {
          ally: allyCode,
          per_page: 1,
        },
      });
      const item = Array.isArray(data?.data) ? data.data[0] : Array.isArray(data) ? data[0] : null;
      if (item?.id) {
        detailCache.set(item.id, item);
      }
      return item || null;
    } catch (error) {
      console.error("Ally not found for code", allyCode, error);
      return null;
    }
  };

  return {
    allies,
    pagination,
    categories,
    cities,
    availableCategories,
    availableCities,
    isLoadingList,
    listError,
    isLoadingDetail,
    detailError,
    fetchAlliesList,
    fetchAllyDetail,
    fetchAllyByCode,
  };
};

export default useAllies;
